/**
 * Single source of truth for purchasable augment styles (website store + lambda /store/buy).
 *
 * Each catalog group is one store row type (home/away variants share a group).
 * Each style entry defines the style id and coin price validated on purchase.
 */

const ONE_GOAL_STORE_ID = 'g';
const PENALTY_UI_VALUE = 'penalty';
const MISSED_PENALTY_UI_VALUE = 'missed-pk';
const RED_CARD_UI_VALUE = 'red-card';

const STYLE_ID = 1;
const FIRE_STYLE_ID = 2;
const HAND_DRAWN_STYLE_ID = 3;
const RISOGRAPH_STYLE_ID = 4;
const BRUTALIST_STYLE_ID = 5;
const COMIC_STYLE_ID = 6;
const CLAYMORPHISM_STYLE_ID = 7;
const GOLD_STYLE_ID = 8;
const SILVER_STYLE_ID = 9;
const CYBERPUNK_STYLE_ID = 10;
const TROPICAL_STYLE_ID = 11;
const BLOOM_STYLE_ID = 12;
const JELLY_STYLE_ID = 13;
const CRYSTAL_STYLE_ID = 14;
const BRAZIL_STYLE_ID = 15;
const WINTER_STYLE_ID = 16;
const PENALTY_FAMILY_STYLE_ID = 101;

/** Coin price per style id (validated on purchase). */
const STORE_STYLE_COSTS = {
  [STYLE_ID]: 1,
  [FIRE_STYLE_ID]: 1,
  [HAND_DRAWN_STYLE_ID]: 1,
  [RISOGRAPH_STYLE_ID]: 1,
  [BRUTALIST_STYLE_ID]: 1,
  [COMIC_STYLE_ID]: 1,
  [CLAYMORPHISM_STYLE_ID]: 1,
  [GOLD_STYLE_ID]: 3,
  [SILVER_STYLE_ID]: 2,
  [CYBERPUNK_STYLE_ID]: 1,
  [TROPICAL_STYLE_ID]: 1,
  [BLOOM_STYLE_ID]: 1,
  [JELLY_STYLE_ID]: 1,
  [CRYSTAL_STYLE_ID]: 1,
  [BRAZIL_STYLE_ID]: 1,
  [WINTER_STYLE_ID]: 1,
  [PENALTY_FAMILY_STYLE_ID]: 7
};

/** ISO date (YYYY-MM-DD) when each style became available. */
const STORE_STYLE_RELEASE_DATES = {
  [STYLE_ID]: '2026-01-01',
  [FIRE_STYLE_ID]: '2026-06-05',
  [HAND_DRAWN_STYLE_ID]: '2026-06-05',
  [RISOGRAPH_STYLE_ID]: '2026-06-05',
  [BRUTALIST_STYLE_ID]: '2026-05-25',
  [COMIC_STYLE_ID]: '2026-06-05',
  [CLAYMORPHISM_STYLE_ID]: '2026-06-06',
  [GOLD_STYLE_ID]: '2026-07-18',
  [SILVER_STYLE_ID]: '2026-07-18',
  [CYBERPUNK_STYLE_ID]: '2026-07-26',
  [TROPICAL_STYLE_ID]: '2026-07-26',
  [BLOOM_STYLE_ID]: '2026-07-26',
  [JELLY_STYLE_ID]: '2026-07-26',
  [CRYSTAL_STYLE_ID]: '2026-07-26',
  [BRAZIL_STYLE_ID]: '2026-07-26',
  [WINTER_STYLE_ID]: '2026-07-26',
  [PENALTY_FAMILY_STYLE_ID]: '2026-06-06'
};

/** @deprecated Use STORE_STYLE_COSTS[STYLE_ID] */
const DEFAULT_STYLE_COST = STORE_STYLE_COSTS[STYLE_ID];

/** UI display names keyed by style id (same name across all augment types). */
const STORE_STYLE_NAMES = {
  [STYLE_ID]: '8-bit',
  [FIRE_STYLE_ID]: 'Fire',
  [HAND_DRAWN_STYLE_ID]: 'Hand-drawn',
  [RISOGRAPH_STYLE_ID]: 'Risograph',
  [BRUTALIST_STYLE_ID]: 'Brutalist',
  [COMIC_STYLE_ID]: 'Comic',
  [CLAYMORPHISM_STYLE_ID]: 'Claymorphism',
  [GOLD_STYLE_ID]: 'Gold',
  [SILVER_STYLE_ID]: 'Silver',
  [CYBERPUNK_STYLE_ID]: 'Cyberpunk',
  [TROPICAL_STYLE_ID]: 'Tropical',
  [BLOOM_STYLE_ID]: 'Bloom',
  [JELLY_STYLE_ID]: 'Jelly',
  [CRYSTAL_STYLE_ID]: 'Crystal',
  [BRAZIL_STYLE_ID]: 'Brazil',
  [WINTER_STYLE_ID]: 'Winter',
  [PENALTY_FAMILY_STYLE_ID]: 'Penaldo'
};

function getStoreStyleName(styleId) {
  return STORE_STYLE_NAMES[styleId] ?? `Style ${styleId}`;
}

const DEFAULT_STORE_STYLE_NAME = getStoreStyleName(STYLE_ID);

const ROUND_AUGMENT_CODES = ['os', 'aon', 'gc', 'bus', 'sn', 'bl'];
const CLASH_AUGMENT_CODES = ['sa', 'bs', 'eif', 'gd'];
/** Clash winner badge store groups (not match augments). */
const WINNER_ICON_CODES = ['c', 'l', 'f'];

function getStoreStyleReleaseDate(styleId) {
  return STORE_STYLE_RELEASE_DATES[styleId] ?? '1970-01-01';
}

/** Newest release first; tie-break by higher style id. */
function compareStyleIdsByReleaseDesc(a, b) {
  const dateCmp = getStoreStyleReleaseDate(b).localeCompare(getStoreStyleReleaseDate(a));
  if (dateCmp !== 0) return dateCmp;
  return b - a;
}

function sortStyleIdsByReleaseDesc(ids) {
  return [...new Set(ids.map((x) => Number(x)).filter((n) => Number.isInteger(n) && n > 0))].sort(
    compareStyleIdsByReleaseDesc
  );
}

/** @param {number} [cost] */
function styleEntry(id, cost = STORE_STYLE_COSTS[id]) {
  return { id, cost, name: getStoreStyleName(id), releaseDate: getStoreStyleReleaseDate(id) };
}

/** Default purchasable styles for a group (Scored/Missed PK also offer style 101). */
function defaultStylesForGroup(groupId) {
  const styles =
    groupId === PENALTY_UI_VALUE || groupId === MISSED_PENALTY_UI_VALUE
      ? [
          styleEntry(STYLE_ID),
          styleEntry(FIRE_STYLE_ID),
          styleEntry(HAND_DRAWN_STYLE_ID),
          styleEntry(RISOGRAPH_STYLE_ID),
          styleEntry(BRUTALIST_STYLE_ID),
          styleEntry(COMIC_STYLE_ID),
          styleEntry(CLAYMORPHISM_STYLE_ID),
          styleEntry(GOLD_STYLE_ID),
          styleEntry(SILVER_STYLE_ID),
          styleEntry(CYBERPUNK_STYLE_ID),
          styleEntry(TROPICAL_STYLE_ID),
          styleEntry(BLOOM_STYLE_ID),
          styleEntry(JELLY_STYLE_ID),
          styleEntry(CRYSTAL_STYLE_ID),
          styleEntry(BRAZIL_STYLE_ID),
          styleEntry(WINTER_STYLE_ID),
          styleEntry(PENALTY_FAMILY_STYLE_ID)
        ]
      : [
          styleEntry(STYLE_ID),
          styleEntry(FIRE_STYLE_ID),
          styleEntry(HAND_DRAWN_STYLE_ID),
          styleEntry(RISOGRAPH_STYLE_ID),
          styleEntry(BRUTALIST_STYLE_ID),
          styleEntry(COMIC_STYLE_ID),
          styleEntry(CLAYMORPHISM_STYLE_ID),
          styleEntry(GOLD_STYLE_ID),
          styleEntry(SILVER_STYLE_ID),
          styleEntry(CYBERPUNK_STYLE_ID),
          styleEntry(TROPICAL_STYLE_ID),
          styleEntry(BLOOM_STYLE_ID),
          styleEntry(JELLY_STYLE_ID),
          styleEntry(CRYSTAL_STYLE_ID),
          styleEntry(BRAZIL_STYLE_ID),
          styleEntry(WINTER_STYLE_ID)
        ];
  return styles.sort((a, b) => compareStyleIdsByReleaseDesc(a.id, b.id));
}

/** @type {{ id: string, codes: string[], styles: { id: number, cost: number, name: string, releaseDate: string }[] }[]} */
const STORE_CATALOG = [
  { id: '2x', codes: ['2x'], styles: defaultStylesForGroup('2x') },
  { id: 's', codes: ['s'], styles: defaultStylesForGroup('s') },
  { id: 'p', codes: ['p'], styles: defaultStylesForGroup('p') },
  { id: 'h', codes: ['h'], styles: defaultStylesForGroup('h') },
  { id: 'd', codes: ['d'], styles: defaultStylesForGroup('d') },
  { id: 'm', codes: ['m'], styles: defaultStylesForGroup('m') },
  { id: 'b', codes: ['b'], styles: defaultStylesForGroup('b') },
  { id: ONE_GOAL_STORE_ID, codes: ['g', '+1h', '+1a'], styles: defaultStylesForGroup(ONE_GOAL_STORE_ID) },
  { id: PENALTY_UI_VALUE, codes: ['hp', 'ap'], styles: defaultStylesForGroup(PENALTY_UI_VALUE) },
  { id: MISSED_PENALTY_UI_VALUE, codes: ['mph', 'mpa'], styles: defaultStylesForGroup(MISSED_PENALTY_UI_VALUE) },
  { id: RED_CARD_UI_VALUE, codes: ['rch', 'rca'], styles: defaultStylesForGroup(RED_CARD_UI_VALUE) },
  { id: 'os', codes: ['os'], styles: defaultStylesForGroup('os') },
  { id: 'aon', codes: ['aon'], styles: defaultStylesForGroup('aon') },
  { id: 'gc', codes: ['gc'], styles: defaultStylesForGroup('gc') },
  { id: 'bus', codes: ['bus'], styles: defaultStylesForGroup('bus') },
  { id: 'sn', codes: ['sn'], styles: defaultStylesForGroup('sn') },
  { id: 'bl', codes: ['bl'], styles: defaultStylesForGroup('bl') },
  { id: 'sa', codes: ['sa'], styles: defaultStylesForGroup('sa') },
  { id: 'bs', codes: ['bs'], styles: defaultStylesForGroup('bs') },
  { id: 'eif', codes: ['eif'], styles: defaultStylesForGroup('eif') },
  { id: 'gd', codes: ['gd'], styles: defaultStylesForGroup('gd') },
  { id: 'c', codes: ['c'], styles: defaultStylesForGroup('c') },
  { id: 'l', codes: ['l'], styles: defaultStylesForGroup('l') },
  { id: 'f', codes: ['f'], styles: defaultStylesForGroup('f') }
];

/** @deprecated Use STORE_CATALOG */
const STORE_STYLE_GROUPS = STORE_CATALOG.map((g) => ({
  id: g.id,
  codes: g.codes,
  styleIds: g.styles.map((s) => s.id)
}));

const GROUP_BY_ID = new Map(STORE_CATALOG.map((g) => [g.id, g]));

/** @type {Map<string, number>} */
const COST_BY_KEY = new Map();
for (const g of STORE_CATALOG) {
  for (const style of g.styles) {
    COST_BY_KEY.set(`${g.id}#${style.id}`, style.cost);
    for (const code of g.codes) {
      COST_BY_KEY.set(`${code}#${style.id}`, style.cost);
    }
  }
}

function findStoreGroupForAugment(augment) {
  return (
    STORE_CATALOG.find((x) => x.codes.length === 1 && x.codes[0] === augment) ??
    STORE_CATALOG.find((x) => x.codes.includes(augment)) ??
    null
  );
}

function getStoreStyleCost(augmentOrGroupId, styleId) {
  return COST_BY_KEY.get(`${augmentOrGroupId}#${styleId}`);
}

function isStoreStyleItem(augmentOrGroupId, styleId) {
  return COST_BY_KEY.has(`${augmentOrGroupId}#${styleId}`);
}

function resolveStorePurchase(augment, storeGroup, styleId) {
  const sid = Number(styleId);
  if (!Number.isInteger(sid) || sid <= 0) return null;
  if (storeGroup && typeof storeGroup === 'string') {
    const g = GROUP_BY_ID.get(storeGroup);
    if (!g) return null;
    const cost = getStoreStyleCost(storeGroup, sid);
    if (cost == null) return null;
    return { codes: [...g.codes], cost };
  }
  if (augment && typeof augment === 'string') {
    const g = findStoreGroupForAugment(augment);
    if (!g) return null;
    const cost = getStoreStyleCost(g.id, sid);
    if (cost == null) return null;
    return { codes: [...g.codes], cost };
  }
  return null;
}

const MATCH_AUGMENT_CODES = STORE_CATALOG.filter(
  (g) =>
    !CLASH_AUGMENT_CODES.includes(g.id) &&
    !ROUND_AUGMENT_CODES.includes(g.id) &&
    !WINNER_ICON_CODES.includes(g.id)
).flatMap((g) => g.codes);

/** Winner-icon SKUs offered per rotation window (capped by catalog size). */
const STORE_WINNER_OFFERING_COUNT = 12;
/** Augment-style SKUs offered per rotation window (capped by catalog size). */
const STORE_AUGMENT_OFFERING_COUNT = 16;
/** Target SKUs per window (winner + augment pools). */
const STORE_OFFERING_COUNT = STORE_WINNER_OFFERING_COUNT + STORE_AUGMENT_OFFERING_COUNT;
/** Rotation period in milliseconds (8 hours). */
const STORE_ROTATION_MS = 8 * 60 * 60 * 1000;

/** Mulberry32 seeded PRNG — returns values in [0, 1). */
function mulberry32(seed) {
  let t = seed >>> 0;
  return function next() {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher–Yates shuffle using a seeded next() in [0, 1). */
function seededShuffle(arr, next) {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Pick up to `targetCount` unique SKUs from groups, round-robin balanced
 * (loads differ by at most 1 among groups that still have styles).
 * @param {{ id: string, styles: { id: number }[] }[]} groups
 * @param {number} targetCount
 * @param {() => number} next
 * @returns {{ groupId: string, styleId: number }[]}
 */
function pickOfferingsFromGroups(groups, targetCount, next) {
  const sellable = groups.filter((g) => g.styles.length > 0);
  if (sellable.length === 0 || targetCount <= 0) return [];

  const maxSkus = sellable.reduce((n, g) => n + g.styles.length, 0);
  const count = Math.min(targetCount, maxSkus);
  const shuffled = seededShuffle(sellable, next);
  const queues = shuffled.map((g) => ({
    groupId: g.id,
    styleIds: seededShuffle(
      g.styles.map((s) => s.id),
      next
    )
  }));

  const items = [];
  let round = 0;
  while (items.length < count) {
    let added = false;
    for (const q of queues) {
      if (items.length >= count) break;
      if (round < q.styleIds.length) {
        items.push({ groupId: q.groupId, styleId: q.styleIds[round] });
        added = true;
      }
    }
    if (!added) break;
    round += 1;
  }
  return items;
}

/**
 * Deterministic rotating store offerings (same for everyone in a window).
 * Winner icons and augment styles are separate pools (12 + 16 targets),
 * each balanced across their groups and capped by available SKUs.
 *
 * @param {number} [now=Date.now()]
 * @returns {{ items: { groupId: string, styleId: number }[], windowStart: number, windowEnd: number, windowIndex: number }}
 */
function getStoreOfferings(now = Date.now()) {
  const t = Number(now);
  const ms = Number.isFinite(t) ? t : Date.now();
  const windowIndex = Math.floor(ms / STORE_ROTATION_MS);
  const windowStart = windowIndex * STORE_ROTATION_MS;
  const windowEnd = windowStart + STORE_ROTATION_MS;
  const next = mulberry32(windowIndex >>> 0);

  const winnerGroups = STORE_CATALOG.filter((g) => WINNER_ICON_CODES.includes(g.id));
  const augmentGroups = STORE_CATALOG.filter((g) => !WINNER_ICON_CODES.includes(g.id));
  const items = [
    ...pickOfferingsFromGroups(winnerGroups, STORE_WINNER_OFFERING_COUNT, next),
    ...pickOfferingsFromGroups(augmentGroups, STORE_AUGMENT_OFFERING_COUNT, next)
  ];

  return { items, windowStart, windowEnd, windowIndex };
}

/**
 * Whether a catalog SKU is in the active rotation window.
 * @param {string} groupId
 * @param {number} styleId
 * @param {number} [now=Date.now()]
 */
function isStoreOfferingActive(groupId, styleId, now = Date.now()) {
  const sid = Number(styleId);
  if (!groupId || !Number.isInteger(sid) || sid <= 0) return false;
  const { items } = getStoreOfferings(now);
  return items.some((it) => it.groupId === groupId && it.styleId === sid);
}

module.exports = {
  ONE_GOAL_STORE_ID,
  /** @deprecated Use ONE_GOAL_STORE_ID */
  PLUS_ONE_GOAL_UI_VALUE: ONE_GOAL_STORE_ID,
  PENALTY_UI_VALUE,
  MISSED_PENALTY_UI_VALUE,
  RED_CARD_UI_VALUE,
  STYLE_ID,
  FIRE_STYLE_ID,
  HAND_DRAWN_STYLE_ID,
  RISOGRAPH_STYLE_ID,
  BRUTALIST_STYLE_ID,
  COMIC_STYLE_ID,
  CLAYMORPHISM_STYLE_ID,
  GOLD_STYLE_ID,
  SILVER_STYLE_ID,
  CYBERPUNK_STYLE_ID,
  TROPICAL_STYLE_ID,
  BLOOM_STYLE_ID,
  JELLY_STYLE_ID,
  CRYSTAL_STYLE_ID,
  BRAZIL_STYLE_ID,
  WINTER_STYLE_ID,
  PENALTY_FAMILY_STYLE_ID,
  DEFAULT_STYLE_COST,
  STORE_STYLE_COSTS,
  STORE_STYLE_RELEASE_DATES,
  DEFAULT_STORE_STYLE_NAME,
  STORE_STYLE_NAMES,
  STORE_CATALOG,
  STORE_STYLE_GROUPS,
  ROUND_AUGMENT_CODES,
  CLASH_AUGMENT_CODES,
  WINNER_ICON_CODES,
  MATCH_AUGMENT_CODES,
  STORE_WINNER_OFFERING_COUNT,
  STORE_AUGMENT_OFFERING_COUNT,
  STORE_OFFERING_COUNT,
  STORE_ROTATION_MS,
  getStoreStyleName,
  getStoreStyleReleaseDate,
  compareStyleIdsByReleaseDesc,
  sortStyleIdsByReleaseDesc,
  resolveStorePurchase,
  getStoreStyleCost,
  isStoreStyleItem,
  findStoreGroupForAugment,
  getStoreOfferings,
  isStoreOfferingActive
};
